// Vue & Plugins
import { MaybeRefOrGetter, computed, toValue } from 'vue'

// Composables
import {
  keepPreviousData,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/vue-query'
// Other
import { DateTime } from 'luxon'

import { Area } from '../../areas/models/area'
import { Building } from '../../buildings/models/building'
import { Pagination } from '../../common/models/pagination'
import { ShiftOfferStatus } from '../../employee_shift/models/employeeShiftOffer'
import { Employee, EmployeeID } from '../../employees/models/employee'
// Models
import { SiteID } from '../../sites/models/site'
import { JobRole } from '../../job_roles/models/jobRole'
import { Skill } from '../../skills/models/skill'
import { Shift, ShiftID } from '../models/shift'

import { useEventStore } from '../../common/eventStore'

// Services
import { ShiftSearchForm, fetch, generateExport } from '../shiftService'

import { isRateLimitedError } from '../../shared/api/apiError'
import { fetchAllPages } from '../../shared/api/utils/pagination'
import { ShiftFilter } from '../types/types'

export interface ShiftFilters {
  dateFrom?: MaybeRefOrGetter<DateTime>
  dateTo?: MaybeRefOrGetter<DateTime>
  byCreatedDate?: MaybeRefOrGetter<boolean>

  status?: MaybeRefOrGetter<'active' | 'cancelled' | 'closed' | undefined>
  fillStatus?: MaybeRefOrGetter<
    'all' | 'filled' | 'unfilled' | 'awaiting_approval'
  >

  visibility?: MaybeRefOrGetter<'all' | 'open' | 'targeted'>

  inProgress?: MaybeRefOrGetter<boolean | undefined>
  inProgressBuffer?: MaybeRefOrGetter<number>

  hasResponses?: MaybeRefOrGetter<boolean>

  building?: MaybeRefOrGetter<Building | null>
  area?: MaybeRefOrGetter<Area | null>
  skill?: MaybeRefOrGetter<Skill | null>
  jobRole?: MaybeRefOrGetter<JobRole | null>
  creator?: MaybeRefOrGetter<Employee | null>
  employee?: MaybeRefOrGetter<Employee | null>
  employeeId?: MaybeRefOrGetter<EmployeeID | null>
  employeeOfferStatus?: MaybeRefOrGetter<ShiftOfferStatus | null>

  rateMin?: MaybeRefOrGetter<number | null>
  rateMax?: MaybeRefOrGetter<number | null>

  perPage?: MaybeRefOrGetter<number>
  page?: MaybeRefOrGetter<number>
  /** Fetch every page for the filters instead of just `page` */
  allPages?: MaybeRefOrGetter<boolean>

  filter?: MaybeRefOrGetter<ShiftFilter>
}

export const shiftKeys = {
  all: ['shifts'] as const,
  lists: () => [...shiftKeys.all, 'list'] as const,
  list: (
    siteId: MaybeRefOrGetter<SiteID | null | undefined>,
    filters: ShiftFilters,
  ) =>
    [
      ...shiftKeys.lists(),
      siteId,
      {
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        perPage: filters.perPage,
        page: filters.page,
        allPages: filters.allPages,
        inProgress: filters.inProgress,
        inProgressBuffer: filters.inProgressBuffer,
        hasResponses: filters.hasResponses,
        building: filters.building,
        area: filters.area,
        skill: filters.skill,
        jobRole: filters.jobRole,
        status: filters.status,
        fillStatus: filters.fillStatus,
        visibility: filters.visibility,
        rateMin: filters.rateMin,
        rateMax: filters.rateMax,
        creator: filters.creator,
        employee: filters.employee,
        employeeId: filters.employeeId,
        employeeOfferStatus: filters.employeeOfferStatus,
        byCreatedDate: filters.byCreatedDate,
        filter: filters.filter,
      },
    ] as const,
  listInfinite: (siteId: MaybeRefOrGetter<SiteID>, filters: ShiftFilters) =>
    [
      ...shiftKeys.lists(),
      'infinite',
      siteId,
      {
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        perPage: filters.perPage,
        page: filters.page,
        allPages: filters.allPages,
        inProgress: filters.inProgress,
        inProgressBuffer: filters.inProgressBuffer,
        hasResponses: filters.hasResponses,
        building: filters.building,
        area: filters.area,
        skill: filters.skill,
        jobRole: filters.jobRole,
        status: filters.status,
        fillStatus: filters.fillStatus,
        visibility: filters.visibility,
        rateMin: filters.rateMin,
        rateMax: filters.rateMax,
        creator: filters.creator,
        employee: filters.employee,
        employeeId: filters.employeeId,
        employeeOfferStatus: filters.employeeOfferStatus,
        byCreatedDate: filters.byCreatedDate,
        filter: filters.filter,
      },
    ] as const,
  details: () => [...shiftKeys.all, 'detail'] as const,
  detail: (siteId: MaybeRefOrGetter<SiteID>, id: MaybeRefOrGetter<ShiftID>) =>
    [...shiftKeys.details(), siteId, id] as const,
  listForEmployee: (filters: ShiftFilters) =>
    [
      ...shiftKeys.lists(),
      'employee',
      { perPage: filters.perPage, page: filters.page },
    ] as const,
}

const filtersToForm = (filters: ShiftFilters): ShiftSearchForm => {
  return {
    dateFrom: toValue(filters.dateFrom),
    dateTo: toValue(filters.dateTo),
    byCreatedDate: toValue(filters.byCreatedDate),

    status: toValue(filters.status),
    fillStatus: toValue(filters.fillStatus),

    visibility: toValue(filters.visibility),

    inProgress: toValue(filters.inProgress) ?? undefined,
    inProgressBuffer: toValue(filters.inProgressBuffer) ?? 0,

    hasResponses: toValue(filters.hasResponses) ?? undefined,

    building: toValue(filters.building),
    area: toValue(filters.area),
    skill: toValue(filters.skill),
    jobRole: toValue(filters.jobRole),
    creator: toValue(filters.creator),
    employee: toValue(filters.employee),
    employeeId: toValue(filters.employeeId),
    employeeOfferStatus: toValue(filters.employeeOfferStatus),

    rateMin: toValue(filters.rateMin) ?? undefined,
    rateMax: toValue(filters.rateMax) ?? undefined,

    perPage: toValue(filters.perPage) ?? 15,
    page: toValue(filters.page) ?? 1,

    filter: toValue(filters.filter),
  }
}

export function useShiftQuery<T = { shifts: Shift[]; pagination: Pagination }>(
  siteId: MaybeRefOrGetter<SiteID | undefined | null>,
  filters: ShiftFilters = {},
  options: {
    enabled?: MaybeRefOrGetter<boolean>
    select?: (shifts: { shifts: Shift[]; pagination: Pagination }) => T
    /** How long a result stays fresh, so a remounted observer reads the cache instead of refetching */
    staleTime?: number
  } = {},
) {
  const queryFn = computed(() => {
    const siteIdValue = toValue(siteId)
    return siteIdValue
      ? async () => {
          const form = filtersToForm(filters)

          if (!toValue(filters.allPages)) {
            return fetch(siteIdValue, form)
          }

          const { items, pagination } = await fetchAllPages(async (page) => {
            const result = await fetch(siteIdValue, { ...form, page })

            return { items: result.shifts, pagination: result.pagination }
          })

          return { shifts: items, pagination }
        }
      : skipToken
  })

  return useQuery({
    queryKey: shiftKeys.list(siteId, filters),
    queryFn: queryFn,
    placeholderData: keepPreviousData,
    select: options?.select,
    enabled: options.enabled,
    staleTime: options.staleTime,
  })
}

export function useShiftQueryInfinite(
  siteId: MaybeRefOrGetter<SiteID>,
  filters: ShiftFilters = {},
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  return useInfiniteQuery({
    queryKey: shiftKeys.listInfinite(siteId, filters),
    queryFn: async ({ pageParam }) => {
      const form = filtersToForm(filters)

      form.perPage = 15
      form.page = pageParam ?? 1

      return await fetch(toValue(siteId), form)
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.pagination.currentPage + 1

      if (nextPage > lastPage.pagination.totalPages) {
        return undefined
      }

      return nextPage
    },
    enabled: options.enabled,
  })
}

export function useShiftExportMutation(siteId: MaybeRefOrGetter<SiteID>) {
  const eventStore = useEventStore()

  return useMutation<void, Error, { form: ShiftFilters }>({
    mutationFn: async (variables) => {
      return generateExport(toValue(siteId), filtersToForm(variables.form))
    },
    onSuccess: async () => {
      eventStore.addToast({
        severity: 'success',
        summary: 'Export Started',
        detail:
          'Your export has been started and will be emailed to you when it is complete.',
      })
    },
    onError: async (error: Error) => {
      if (isRateLimitedError(error)) {
        eventStore.addErrorToast({ detail: error.message })
      }
    },
  })
}
